"use client";

import "./callWindow.css";
import { useCallback, useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LiveAudioVisualizer } from "react-audio-visualize";
import placeholder from "@/assets/placeholder.png";
import CallHintsDisplay from "./CallHintsDisplay";

import {
  LiveKitRoom,
  useVoiceAssistant,
  BarVisualizer,
  RoomAudioRenderer,
  VoiceAssistantControlBar,
  AgentState,
  DisconnectButton
} from "@livekit/components-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MediaDeviceFailure } from "livekit-client";
import { NoAgentNotification } from "@/components/ui/NoAgentNotification";
import { CloseIcon } from "@/components/ui/CloseIcon";
import { useKrispNoiseFilter } from "@livekit/components-react/krisp";
import { TtsConfig } from "../hooks/useVoiceTtsConfigs";

interface LivekitVoiceProps {
  isCallDialogOpen: boolean;
  setIsCallDialogOpen: (isOpen: boolean) => void;
  phoneNumber: string;
  name: string;
  callHints: any[];
  callHintsTable: any[];
  agentName: string;
  icon: string;
  voiceConfig: {
    tokenUrl: string;
  };
  ttsConfig: TtsConfig;
}

export default function LivekitVoice({
  isCallDialogOpen,
  setIsCallDialogOpen,
  phoneNumber,
  name,
  callHints,
  callHintsTable,
  agentName,
  ttsConfig,
  voiceConfig,
  icon
}: LivekitVoiceProps) {
  type ConnectionDetails = {
    serverUrl: string;
    roomName: string;
    participantName: string;
    participantToken: string;
    voiceConfig: {
      tokenUrl: string;
    };
    ttsConfig: any;
  };

  const [connectionDetails, updateConnectionDetails] = useState<
    ConnectionDetails | undefined
  >(undefined);
  const [agentState, setAgentState] = useState<AgentState>("disconnected");

  const connectCall = async () => {
    const url = voiceConfig.tokenUrl;
    const response = await fetch(url);
    const connectionDetailsData = await response.json();
    updateConnectionDetails({
      ...connectionDetailsData,
      metadata: {
        ttsConfig: ttsConfig,
        phoneNumber: phoneNumber
      }
    });
  };

  useEffect(() => {
    connectCall();
    return () => {
      updateConnectionDetails(undefined);
    };
  }, []);

  return (
    <Dialog
      open={isCallDialogOpen}
      onOpenChange={(open) => {
        // disabled due to call disconnect issues
        // setIsCallDialogOpen(open);
        if (!open) updateConnectionDetails(undefined);
      }}
    >
      <DialogContent className="max-w-[800px] grid content-center bg-[var(--lk-bg)] bg-white">
        <main
          data-lk-theme="default"
          className="h-full grid content-center bg-[var(--lk-bg)]"
        >
          {/* @ts-ignore */}
          <LiveKitRoom
            token={connectionDetails?.participantToken}
            serverUrl={connectionDetails?.serverUrl}
            connect={connectionDetails !== undefined}
            audio={true}
            video={false}
            onConnected={connectCall}
            onMediaDeviceFailure={onDeviceFailure}
            onDisconnected={() => {
              updateConnectionDetails(undefined);
            }}
            className="grid grid-rows-[2fr_1fr] items-center"
          >
            <VoiceAssistantAnimation
              onStateChange={setAgentState}
              name={name}
              callHints={callHints}
              callHintsTable={callHintsTable}
              agentName={agentName}
              disconnectCall={() => {
                setIsCallDialogOpen(false);
              }}
              icon={icon}
            />

            <RoomAudioRenderer />
            <NoAgentNotification state={agentState} />
          </LiveKitRoom>
        </main>
      </DialogContent>
    </Dialog>
  );
}
function VoiceAssistantAnimation({
  onStateChange,
  callHints,
  callHintsTable,
  agentName,
  disconnectCall,
  icon
}: {
  onStateChange: Function;
  name: string;
  callHints: any[];
  callHintsTable: any[];
  agentName: string;
  disconnectCall: Function;
  icon: string;
}) {
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const { state } = useVoiceAssistant();
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [stream, setStream] = useState<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        setStream(stream);

        audioContextRef.current = new AudioContext();
        const source = audioContextRef.current.createMediaStreamSource(stream);

        const highPassFilter = audioContextRef.current.createBiquadFilter();
        highPassFilter.type = "highpass";
        highPassFilter.frequency.setValueAtTime(
          400,
          audioContextRef.current.currentTime
        );

        source.connect(highPassFilter);
        const destination =
          audioContextRef.current.createMediaStreamDestination();
        highPassFilter.connect(destination);

        const mediaRecorder = new MediaRecorder(destination.stream);
        setMediaRecorder(mediaRecorder);
        mediaRecorder.start();

        mediaRecorder.onerror = (event) => {
          console.error("MediaRecorder error:", (event as ErrorEvent).error);
        };
      } catch (error) {
        console.error("Failed to access microphone:", error);
      }
    };

    startRecording();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (mediaRecorder) {
        mediaRecorder.stop();
      }
    };
  }, []);

  useEffect(() => {
    onStateChange(state);
  }, [state]);

  useEffect(() => {
    const timer = setInterval(() => {
      setSeconds((seconds) => seconds + 1);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <div className="h-[350px] max-w-[90vw] mx-auto">
      <div className="voice-call-container">
        <div className="callcontainer">
          <div className="grid-wrapper">
            <div className="block-main">
              <div className="nurix-icon-wrapper">
                <div className="nurix-icon">
                  <img className="x-icon is-small" src={icon} alt="AI Agent" />
                </div>
                <div className="font-24" style={{ marginTop: "-40px" }}>
                  <b>{agentName}</b>
                </div>
              </div>

              <div className="sound-visualizer-wrapper">
                <div>
                  {mediaRecorder && (
                    <div style={{ display: "flex", gap: "3px" }}>
                      <div style={{ transform: "scaleX(-1)" }}>
                        <LiveAudioVisualizer
                          mediaRecorder={mediaRecorder}
                          width={150}
                          height={70}
                          barWidth={3}
                          gap={3}
                          barColor={"#3b82f6"}
                          fftSize={256}
                          minDecibels={-70}
                          smoothingTimeConstant={0.9}
                        />
                      </div>
                      <div>
                        <LiveAudioVisualizer
                          mediaRecorder={mediaRecorder}
                          width={150}
                          height={70}
                          barWidth={3}
                          gap={3}
                          barColor={"#3b82f6"}
                          fftSize={256}
                          minDecibels={-70}
                          smoothingTimeConstant={0.9}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="call-timer">{formatTime(seconds)}</div>
              </div>

              <div className="image-icon-wrapper">
                <div className="image-icon">
                  <img src={placeholder} alt="You" />
                </div>
                <div className="font-24" style={{ marginTop: "-20px" }}>
                  <b>You</b>
                </div>
              </div>
            </div>
          </div>

          <CallHintsDisplay
            callHints={callHints}
            callHintsTable={callHintsTable}
          />

          <div className="btn-wrapper-is-margin pb-10">
            {/* @ts-ignore */}

            <button
              className="call-nurix-button is-red"
              onClick={() => {
                disconnectCall();
              }}
            >
              End Call
            </button>
          </div>
          <span className="opacity-30 mt-3">
            This AI agent is a demo and may not provide fully accurate responses
            or take any actions.
          </span>
        </div>
      </div>
    </div>
  );
}

function ControlBar(props: {
  onConnectButtonClicked: () => void;
  agentState: AgentState;
}) {
  /**
   * Use Krisp background noise reduction when available.
   * Note: This is only available on Scale plan, see {@link https://livekit.io/pricing | LiveKit Pricing} for more details.
   */
  const krisp = useKrispNoiseFilter();
  useEffect(() => {
    krisp.setNoiseFilterEnabled(true);
  }, []);

  return (
    <div className="relative h-[100px]">
      <AnimatePresence>
        {props.agentState === "disconnected" && (
          <motion.button
            initial={{ opacity: 0, top: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, top: "-10px" }}
            transition={{ duration: 1, ease: [0.09, 1.04, 0.245, 1.055] }}
            className="uppercase absolute left-1/2 -translate-x-1/2 px-4 py-2 bg-white text-black rounded-md"
            onClick={() => props.onConnectButtonClicked()}
          >
            Start a conversation
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {props.agentState !== "disconnected" &&
          props.agentState !== "connecting" && (
            <motion.div
              initial={{ opacity: 0, top: "10px" }}
              animate={{ opacity: 1, top: 0 }}
              exit={{ opacity: 0, top: "-10px" }}
              transition={{ duration: 0.4, ease: [0.09, 1.04, 0.245, 1.055] }}
              className="flex h-8 absolute left-1/2 -translate-x-1/2  justify-center"
            >
              <VoiceAssistantControlBar controls={{ leave: false }} />
              {/* @ts-ignore */}
              <DisconnectButton>
                <CloseIcon />
              </DisconnectButton>
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
}

function onDeviceFailure(error?: MediaDeviceFailure) {
  console.error(error);
  alert(
    "Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab"
  );
}
